/**
 * admin/audit.tsx — Audit log surface.
 *
 * Lives at `/admin/audit`. Auth-gated to admins. Renders <AuditTable>.
 * Defaults to page 1, 20 entries per page.
 */

import type { ReactNode } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { AuditTable } from '../../components/admin/AuditTable.js';
import { freshSessionQuery, auditQuery, type SessionPrincipal } from '../../lib/api/queries.js';

export const Route = createFileRoute('/admin/audit')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    return null;
  },
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(auditQuery(1, 20));
    return null;
  },
  component: AdminAuditPage,
});

function AdminAuditPage(): ReactNode {
  return (
    <main className="page-shell" data-testid="admin-audit-page">
      <PageHeader
        eyebrow="Operasional"
        title="Log aktivitas"
        description="Jejak aktivitas yang tercatat otomatis: setujui tutor, buat dan tukar kode undangan, dan lainnya."
      />
      <section className="section">
        <AuditTable initialPage={1} pageSize={20} />
      </section>
    </main>
  );
}
